import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "searchInput", "searchResults", "searchSpinner", "searchSection",
    "studentInfo", "studentName", "studentIdField",
    "parentInfo", "parentIdField",
    "sepsContainer", "sepsTableBody", "noSeps",
    "typeSection", "typeDeadlineExtension", "typeInstallmentPlan",
    "deadlineSection", "installmentSection",
    "remainingAmount", "installmentRows", "installmentTotal", "installmentMismatch",
    "selectedSepId"
  ]

  static values = {
    searchUrl: String,
    // The student of a refused moratorium, chosen again (QA R2-09 g).
    chosenStudent: Object
  }

  connect() {
    this.formatter = new Intl.NumberFormat("fr-FR", { useGrouping: true })
    this.selectedSep = null
    this.searchTimeout = null
    this.selectedStudentData = null
    // Both type sections start hidden: disable their fields so a hidden required
    // control (installment amount/date) never blocks form submission.
    this.setSectionFieldsDisabled(this.deadlineSectionTarget, true)
    this.setSectionFieldsDisabled(this.installmentSectionTarget, true)
    if (this.hasChosenStudentValue && this.chosenStudentValue.id) {
      this.applyStudent(this.chosenStudentValue)
    }
  }

  disconnect() {
    if (this.searchTimeout) clearTimeout(this.searchTimeout)
  }

  // --- Search methods ---

  search() {
    const query = this.searchInputTarget.value.trim()

    if (this.searchTimeout) clearTimeout(this.searchTimeout)

    if (query.length < 2) {
      this.hideSearchResults()
      return
    }

    this.searchTimeout = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  async performSearch(query) {
    this.searchSpinnerTarget.classList.remove("hidden")

    try {
      const url = `${this.searchUrlValue}?q=${encodeURIComponent(query)}`
      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      if (!response.ok) return

      const data = await response.json()
      this.displayResults(data.students)
    } catch (error) {
      console.error("Search error:", error)
    } finally {
      this.searchSpinnerTarget.classList.add("hidden")
    }
  }

  displayResults(students) {
    const container = this.searchResultsTarget
    container.innerHTML = ""

    if (students.length === 0) {
      container.innerHTML = `
        <div class="px-4 py-3 text-sm text-gray-500">
          Aucun élève trouvé
        </div>
      `
      container.classList.remove("hidden")
      return
    }

    students.forEach((student) => {
      const item = document.createElement("div")
      item.classList.add(
        "px-4", "py-3", "cursor-pointer", "hover:bg-gray-50",
        "border-b", "border-gray-100", "last:border-b-0"
      )
      item.dataset.action = "click->moratorium-form#selectStudent"
      item.dataset.student = JSON.stringify(student)

      const classroom = student.classroom ? ` — ${student.classroom}` : ""
      item.innerHTML = `
        <div class="flex items-center justify-between">
          <div>
            <span class="text-sm font-medium text-gray-900">${this.escapeHtml(student.full_name)}</span>
            <span class="text-xs text-gray-500 ml-2">${this.escapeHtml(student.registration_number || "")}</span>
          </div>
          <span class="text-xs text-gray-400">${this.escapeHtml(classroom)}</span>
        </div>
      `
      container.appendChild(item)
    })

    container.classList.remove("hidden")
  }

  hideSearchResults() {
    this.searchResultsTarget.classList.add("hidden")
    this.searchResultsTarget.innerHTML = ""
  }

  selectStudent(event) {
    this.applyStudent(JSON.parse(event.currentTarget.dataset.student))
  }

  applyStudent(studentData) {
    this.selectedStudentData = studentData

    // Hide search, show student info
    this.hideSearchResults()
    this.searchSectionTarget.classList.add("hidden")
    this.studentInfoTarget.classList.remove("hidden")

    // Fill hidden fields
    this.studentIdFieldTarget.value = studentData.id

    // Display student name
    const classroom = studentData.classroom ? ` — ${studentData.classroom}` : ""
    this.studentNameTarget.textContent =
      `${studentData.full_name} (${studentData.registration_number || ""})${classroom}`

    // Parents
    if (studentData.parents && studentData.parents.length > 0) {
      this.parentIdFieldTarget.value = studentData.parents[0].id
      const names = studentData.parents.map(p => p.name).join(", ")
      this.parentInfoTarget.textContent = names
    } else {
      this.parentIdFieldTarget.value = ""
      this.parentInfoTarget.textContent = "—"
    }

    // Load unpaid SEPs
    this.resetSepAndBelow()
    const seps = studentData.unpaid_seps || []
    if (seps.length === 0) {
      this.sepsContainerTarget.classList.remove("hidden")
      this.sepsTableBodyTarget.innerHTML = ""
      this.noSepsTarget.classList.remove("hidden")
      return
    }

    this.noSepsTarget.classList.add("hidden")
    this.showSeps(seps)
  }

  changeStudent() {
    // Reset everything
    this.selectedStudentData = null
    this.studentIdFieldTarget.value = ""
    this.parentIdFieldTarget.value = ""
    this.parentInfoTarget.textContent = "—"
    this.searchInputTarget.value = ""

    // Show search, hide student info
    this.studentInfoTarget.classList.add("hidden")
    this.searchSectionTarget.classList.remove("hidden")
    this.searchInputTarget.focus()

    // Reset SEPs and below
    this.resetSepAndBelow()
    this.hideSeps()
  }

  resetSepAndBelow() {
    this.selectedSep = null
    this.selectedSepIdTarget.value = ""
    this.hideTypeSection()
    this.hideDeadlineSection()
    this.hideInstallmentSection()
  }

  // --- SEP methods ---

  showSeps(seps) {
    const tbody = this.sepsTableBodyTarget
    tbody.innerHTML = ""

    seps.forEach((sep) => {
      const statusClass = this.statusBadgeClass(sep.status)
      const row = document.createElement("tr")
      row.classList.add("cursor-pointer", "hover:bg-gray-50")
      row.innerHTML = `
        <td>
          <input type="radio" name="sep_radio" value="${sep.id}"
                 class="text-primary-600 focus:ring-primary-500"
                 data-action="change->moratorium-form#sepSelected"
                 data-remaining="${sep.remaining}" data-label="${this.escapeHtml(sep.label)}">
        </td>
        <td class="text-sm">${this.escapeHtml(sep.label)}</td>
        <td class="text-sm text-right">${this.formatter.format(sep.amount)} F</td>
        <td class="text-sm text-right font-medium text-red-600">${this.formatter.format(sep.remaining)} F</td>
        <td class="text-sm">${sep.due_date_label || "—"}</td>
        <td>
          <span class="px-2 py-0.5 text-xs rounded-full font-semibold ${statusClass}">${this.escapeHtml(sep.status_label || sep.status)}</span>
        </td>
      `
      tbody.appendChild(row)
    })

    this.sepsContainerTarget.classList.remove("hidden")
  }

  hideSeps() {
    this.sepsContainerTarget.classList.add("hidden")
    this.sepsTableBodyTarget.innerHTML = ""
    this.selectedSepIdTarget.value = ""
  }

  sepSelected(event) {
    const radio = event.target
    this.selectedSepIdTarget.value = radio.value
    this.selectedSep = {
      id: radio.value,
      remaining: parseFloat(radio.dataset.remaining),
      label: radio.dataset.label,
    }

    this.remainingAmountTarget.textContent =
      `${this.formatter.format(this.selectedSep.remaining)} F CFA`

    this.showTypeSection()

    this.typeDeadlineExtensionTarget.checked = false
    this.typeInstallmentPlanTarget.checked = false
    this.hideDeadlineSection()
    this.hideInstallmentSection()
  }

  // --- Type methods ---

  showTypeSection() {
    this.typeSectionTarget.classList.remove("hidden")
  }

  hideTypeSection() {
    this.typeSectionTarget.classList.add("hidden")
  }

  typeChanged(event) {
    const type = event.target.value

    if (type === "deadline_extension") {
      this.showDeadlineSection()
      this.hideInstallmentSection()
    } else if (type === "installment_plan") {
      this.hideDeadlineSection()
      this.showInstallmentSection()
    }
  }

  showDeadlineSection() {
    this.deadlineSectionTarget.classList.remove("hidden")
    this.setSectionFieldsDisabled(this.deadlineSectionTarget, false)
  }

  hideDeadlineSection() {
    this.deadlineSectionTarget.classList.add("hidden")
    // Disable hidden fields so their (empty, required) state can't block submit.
    this.setSectionFieldsDisabled(this.deadlineSectionTarget, true)
  }

  showInstallmentSection() {
    this.installmentSectionTarget.classList.remove("hidden")
    if (this.installmentRowsTarget.children.length === 0) {
      this.addInstallment()
    }
    this.setSectionFieldsDisabled(this.installmentSectionTarget, false)
    this.updateTotal()
  }

  hideInstallmentSection() {
    this.installmentSectionTarget.classList.add("hidden")
    // Disable the hidden installment inputs (they carry `required`): otherwise an
    // empty, non-focusable required field silently blocks the whole form submit.
    this.setSectionFieldsDisabled(this.installmentSectionTarget, true)
  }

  // A `disabled` field is excluded from HTML5 validation AND from form submission,
  // so only the currently visible moratorium-type section is ever validated/sent.
  setSectionFieldsDisabled(sectionTarget, disabled) {
    sectionTarget.querySelectorAll("input, select, textarea").forEach((el) => {
      el.disabled = disabled
    })
  }

  // --- Installment methods ---

  addInstallment() {
    const rows = this.installmentRowsTarget
    const index = rows.children.length
    const position = index + 1

    const row = document.createElement("div")
    row.classList.add("grid", "grid-cols-12", "gap-2", "items-end", "mb-2")
    row.dataset.installmentRow = "true"
    row.innerHTML = `
      <div class="col-span-3">
        <label class="label-style text-xs mb-1">Label</label>
        <input type="text" name="payment_moratorium[moratorium_installments_attributes][${index}][label]"
               value="Tranche ${position}" class="input-style w-full text-sm">
      </div>
      <div class="col-span-3">
        <label class="label-style text-xs mb-1">Montant</label>
        <input type="number" name="payment_moratorium[moratorium_installments_attributes][${index}][amount]"
               class="input-style w-full text-sm" min="1" required
               data-action="input->moratorium-form#updateTotal">
      </div>
      <div class="col-span-3">
        <label class="label-style text-xs mb-1">Date</label>
        <input type="text" name="payment_moratorium[moratorium_installments_attributes][${index}][due_date]"
               class="input-style w-full text-sm" data-controller="flatpickr" data-flatpickr-min-date-value="today" required>
      </div>
      <input type="hidden" name="payment_moratorium[moratorium_installments_attributes][${index}][position]" value="${position}">
      <div class="col-span-3 flex items-center gap-2">
        <button type="button" class="text-red-500 hover:text-red-700 text-sm font-medium"
                data-action="click->moratorium-form#removeInstallment">
          Supprimer
        </button>
      </div>
    `
    rows.appendChild(row)
    this.updateTotal()
  }

  removeInstallment(event) {
    const row = event.target.closest("[data-installment-row]")
    if (row) {
      row.remove()
      this.reindexInstallments()
      this.updateTotal()
    }
  }

  reindexInstallments() {
    const rows = this.installmentRowsTarget.querySelectorAll("[data-installment-row]")
    rows.forEach((row, index) => {
      const position = index + 1
      row.querySelectorAll("input").forEach((input) => {
        const name = input.getAttribute("name")
        if (name) {
          input.setAttribute("name", name.replace(/\[\d+\]/, `[${index}]`))
        }
      })
      const posInput = row.querySelector("input[name*='[position]']")
      if (posInput) posInput.value = position
    })
  }

  updateTotal() {
    const rows = this.installmentRowsTarget.querySelectorAll("[data-installment-row]")
    let total = 0
    rows.forEach((row) => {
      const amountInput = row.querySelector("input[name*='[amount]']")
      if (amountInput && amountInput.value) {
        total += parseFloat(amountInput.value) || 0
      }
    })

    this.installmentTotalTarget.textContent = `${this.formatter.format(total)} F CFA`

    if (this.selectedSep) {
      const remaining = this.selectedSep.remaining
      if (Math.abs(total - remaining) > 0.01 && total > 0) {
        this.installmentMismatchTarget.classList.remove("hidden")
      } else {
        this.installmentMismatchTarget.classList.add("hidden")
      }
    }
  }

  // --- Helpers ---

  statusBadgeClass(status) {
    switch (status) {
      case "paid": return "bg-cta-shared text-cta-darker"
      case "partial_payment": return "bg-yellow-100 text-yellow-700"
      case "overdue": return "bg-red-100 text-red-700"
      default: return "bg-gray-100 text-gray-700"
    }
  }

  escapeHtml(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
